import React, { FC, useEffect, useState } from "react";
import apiClient from "../utils/apiClient";
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import { Rekviz } from "../types/rekviz";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import Provider from "../enum/Provider.enum";
import { useTranslation } from "react-i18next";

type CreateRekvizFormProps = {
  onClose: () => void;
  onSuccess: () => void;
  agentId: number;
  item: Rekviz;
};

const CreateRekvizForm: FC<CreateRekvizFormProps> = ({
  onClose,
  onSuccess,
  agentId,
  item,
}) => {
  const { t } = useTranslation();

  const initialState = {
    r_s: "",
    k_s: "",
    bik: "",
    curr: "",
    recipient: "",
    description: "",
    is_default: false,
    bank_name: "",
    bank_name_am: "",
  };

  const { provider } = useSelector((state: RootState) => state.app);
  const isArmenia = provider === Provider.ARMENIA;

  const [formData, setFormData] = useState(initialState);
  const [saving, setIsSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (item) {
      const newData = { ...initialState, ...item };
      setFormData(newData);
    } else {
      setFormData(initialState);
    }
  }, [item]);

  const handleSave = async () => {
    if (
      !formData.r_s ||
      !formData.k_s ||
      !formData.bik ||
      (isArmenia && !formData.bank_name_am)
    ) {
      alert(
        isArmenia
          ? t("rekviz.validation.requiredArmenia")
          : t("rekviz.validation.required"),
      );
      return;
    }

    try {
      setIsSaving(true);
      const res = await apiClient.post("/rekviz_agents", {
        agent_id: agentId,
        ...formData,
      });
      if (res.data.status === "ok") {
        alert(t("rekviz.saved"));
        onSuccess();
      } else {
        alert(t("rekviz.saveError"));
      }
    } catch (err) {
      alert(t("rekviz.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label={t("rekviz.fields.account")}
            name="r_s"
            value={formData.r_s}
            onChange={handleChange}
            disabled={saving}
            required
            fullWidth
            size="small"
          />

          <TextField
            label={t("rekviz.fields.correspondentAccount")}
            name="k_s"
            value={formData.k_s}
            onChange={handleChange}
            disabled={saving}
            required
            fullWidth
            size="small"
          />

          <TextField
            label={t("rekviz.fields.bik")}
            name="bik"
            value={formData.bik}
            onChange={handleChange}
            disabled={saving}
            required
            fullWidth
            size="small"
          />

          <TextField
            label={t("rekviz.fields.currency")}
            name="curr"
            value={formData.curr}
            onChange={handleChange}
            disabled={saving}
            fullWidth
            size="small"
          />

          <TextField
            label={t("rekviz.fields.recipient")}
            name="recipient"
            value={formData.recipient}
            onChange={handleChange}
            disabled={saving}
            fullWidth
            size="small"
          />

          <TextField
            label={t("rekviz.fields.description")}
            name="description"
            value={formData.description}
            onChange={handleChange}
            disabled={saving}
            fullWidth
            size="small"
          />

          <TextField
            label={t("rekviz.fields.bank")}
            name="bank_name"
            value={formData.bank_name}
            onChange={handleChange}
            disabled={saving}
            fullWidth
            size="small"
          />

          {isArmenia && (
            <TextField
              label={t("rekviz.fields.bankArmenian")}
              name="bank_name_am"
              value={formData.bank_name_am ?? ""}
              onChange={handleChange}
              disabled={saving}
              required
              fullWidth
              size="small"
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                name="is_default"
                checked={formData.is_default}
                disabled={saving}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    is_default: e.target.checked,
                  }))
                }
              />
            }
            label={t("rekviz.fields.isDefault")}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          fullWidth
        >
          {t("common.save")}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={saving}
          fullWidth
        >
          {t("common.cancel")}
        </Button>
      </DialogActions>
    </>
  );
};

export default CreateRekvizForm;
